'use client';

import { Typography } from '@/components/Typography';
import { Carousel } from '@/components/carousel/Carousel';
import { CarouselSlide } from '@/components/carousel/CarouselSlide';
import { PhosphorIcon } from '@/components/icons/PhosphorIcon';
import { breakpointUtils } from '@/lib/breakpoints';
import { cn } from '@/lib/cn';
import type { CmsBlogTag } from '@/services/cms-sdk';
import { useFilteredBlogPostState } from './FilteredBlogPostProvider';

type ToggleTag = (label: string) => void;
type TagProps = Pick<CmsBlogTag, 'icon' | 'title'> & {
    selected?: boolean;
    toggleTag: ToggleTag;
};

function Tag({ icon, title, toggleTag, selected = false }: TagProps) {
    return (
        <CarouselSlide>
            <Typography
                className={cn('flex items-center h-9 w-fit px-2.5 rounded-lg gap-1.5 shrink-0 bg-badge-content cursor-pointer', {
                    'text-primary bg-base-150': !selected,
                    'text-white bg-primary': selected,
                })}
                onClick={() => toggleTag(title.trim().toLowerCase())}
                role="button"
            >
                {icon ? <PhosphorIcon className="text-[1.06rem]" name={icon} /> : null} <span className="leading-[22.4px]">{title}</span>
            </Typography>
        </CarouselSlide>
    );
}

export interface FilteredBlogPostTagsProps {
    tags?: CmsBlogTag[];
}

export function FilteredBlogPostTags({ tags = [] }: FilteredBlogPostTagsProps) {
    const { filters, toggleTag, resetTags } = useFilteredBlogPostState();

    return tags.length > 0 ? (
        <div className="sticky top-0 bg-white z-10">
            <div className="py-4 md:py-6 md:flex md:flex-row md:justify-center">
                <Carousel
                    className="[--carousel-spacing:8px]"
                    options={{
                        loop: false,
                        align: 'start',
                        slidesToScroll: 1,
                        containScroll: 'trimSnaps',
                        skipSnaps: true,
                        breakpoints: {
                            [breakpointUtils.up('md')]: {
                                align: 'center',
                            },
                        },
                    }}
                    showNavigation={false}
                    showPagination={false}
                >
                    <Tag title="All blogs" toggleTag={resetTags} selected={!filters || filters?.length === 0} />
                    {tags.map(({ icon, title }, index) => (
                        <Tag key={`${title}-${index}`} icon={icon} title={title} toggleTag={toggleTag} selected={filters?.includes(title.trim().toLowerCase())} />
                    ))}
                </Carousel>
            </div>
        </div>
    ) : null;
}
